import crypto from 'crypto';
import { mapActions, mapGetters, mapState } from 'vuex';

import { PROJECT_BRANDS } from '@/modules/brand/domain/brand';
import { REGIONS } from '@/modules/region/domain/region';
import { PAYPAL_TYPES } from '@/services/constants';
import { isEmailBlocked } from '@/services/ignored_email_list_from_GTM';

import { getUtmObject, setUtmObject } from '../services/utm';

const touchMap = new WeakMap();

/**
 * Shared global mixins
 * {delayTouch} - to delay the error message and show it after 1s from typing
 */
export const globalMixin = {
  data() {
    return {
      inputType: 'password',
      iconSymbolName: 'icon-eye-strike',
    };
  },
  computed: {
    ...mapGetters('payment', ['getUserEmail', 'getAffiliates']),
    ...mapGetters('detected-country', ['isPrefixUkOrEU']),
    ...mapState({
      userSubscription: (state) => state.common.userSubscription,
      leadUuid: (state) => state['have-lead'].uuid,
      newCheckoutIdToRedirect: (state) => state['checkout'].checkoutId,
    }),
    getPaypalType() {
      return this.isPrefixUkOrEU ? PAYPAL_TYPES.global : PAYPAL_TYPES.us;
    },
    getLanguage() {
      const locales = this.$i18n?.locales ?? [];
      const usedLocale = locales.find((locale) => locale.code === this.$i18n.locale);
      return usedLocale ? usedLocale.acceptedLanguage.substring(0, 2) : this.$i18n.locale;
    },
    // Terms of Service url with language mapping
    tosUrl() {
      if (this.brand === PROJECT_BRANDS.muscleCharge) {
        return this.$getUrl('termsMuscleCharge');
      }

      const locale = this.getLanguage;
      const region = this.$countryCode;

      const params = `?region=${region}&locale=${locale}`;

      return `${this.$getUrl('terms')}${params}`;
    },
    // Privacy Policy url with language mapping
    ppUrl() {
      if (this.brand === PROJECT_BRANDS.muscleCharge) {
        return this.$getUrl('privacyMuscleCharge');
      }

      const locale = this.getLanguage;
      const region = this.$countryCode;

      const params = `?region=${region}&locale=${locale}`;

      return `${this.$getUrl('privacy')}${params}`;
    },
  },

  methods: {
    ...mapActions('payment', ['setAffiliates']),
    ...mapActions('footer', ['changeFooterStatusAction']),
    ...mapActions('common', ['setUTMObjectStore']),
    ...mapActions('have-lead', ['checkIfUserHaveLeadDetails', 'setLeadUUID']),
    delayTouch($v) {
      $v.$reset();
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v));
      }
      touchMap.set($v, setTimeout($v.$touch, 1000));
    },
    scrollToTop() {
      setTimeout(() => {
        window.scroll({
          top: 1,
          left: 0,
          behavior: 'smooth',
        });
      }, 0);
    },
    scrollToBottom() {
      setTimeout(() => {
        window.scrollTo({
          left: 0,
          top: document.body.scrollHeight || document.documentElement.scrollHeight,
          behavior: 'smooth',
        });
      }, 0);
    },
    pushGTMEvent(eventName, eventData = {}) {
      return this.$gtm.push({
        event: eventName,
        ...eventData,
      });
    },
    toggleInputTypeHandler() {
      if (this.inputType === 'password') {
        this.inputType = 'text';
        this.iconSymbolName = 'icon-eye';
      } else {
        this.inputType = 'password';
        this.iconSymbolName = 'icon-eye-strike';
      }
    },
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    isEmailBlocked(email) {
      return isEmailBlocked(email);
    },
    queryHandler() {
      // Loop through url query and store only the ones that startsWith {UTM}
      const { query } = this.$route;
      const validParams = Object.keys(query).filter((key) => key.startsWith('utm'));
      const utmArray = validParams.map((el) => ({
        [el]: query[el] && Array.isArray(query[el]) ? query[el][0] : query[el],
      }));

      const utmObject = Object.assign({}, ...utmArray);

      // Only set it to vuex store if there is value in the array
      !!utmArray.length && (this.setUtmObject(utmObject) || this.setUTMObjectStore(utmObject));

      if (query.aff) {
        this.setAffiliates(true);
      }
    },
    getUtmObject,
    setUtmObject,
    isLandingPageEventPushed() {
      for (let i = 0; i < window?.dataLayer?.length; i++) {
        const eventData = window?.dataLayer[i];
        if (eventData.event === 'landing_page') {
          return true; // landing_page event found
        }
      }
      return false; // landing_page event not found
    },
    tinyHash(data, outputLength = 6) {
      return crypto.createHmac('sha256', 'tinyHash').update(data).digest('hex').substring(0, outputLength);
    },
    removeLastSlash(str) {
      return str.substring(str.length - 1, str.length) === '/' ? str.substring(0, str.length - 1) : str;
    },
    getFeaturedLogos(key = 'logosBestBody') {
      const logos = this.$t(`static.authorityRealatedTestimonials.${key}`);
      const isTelevisaRegion = [REGIONS.br, REGIONS.latam].includes(this.$countryCode);
      return logos.filter((logo) => {
        if (isTelevisaRegion) return logo.img !== 'reviewed-by-usa.webp';
        else return logo.img !== 'televisanetworks.png';
      });
    },
  },
};
